import React, { useState } from 'react';
import FloatingMenu from '../FloatingMenu';
import SlideModal from '../SlideModal'; // Keep this, used by both versions
import Carousel from 'react-multi-carousel'; // Keep this
import 'react-multi-carousel/lib/styles.css'; // Keep this
import { DatabaseIcon, RagEmbeddingsIcon, PromptEngineeringIcon, LangGraphIcon, LLMCoreIcon, AudioIcon, FunctionCallingIcon, GroundingIcon, StructuredOutputIcon, StatefulConversationIcon, GenAIEvaluationIcon } from '../TechIcons'; // Keep this, includes all icons needed

// New imports for technical blog (Keep these)
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { ThemeProvider, Box, Heading, Text, Flex, Card, Badge, Button } from 'theme-ui';

// --- Slide Content Arrays - FROM TechDeepDive_multi_crousel.js (Replacing original smaller set) ---
const dbSlides = [
  {
    title: "🗄️ Slide 1: Databases - Organizing Information 🗄️",
    content: [
      "Think of databases like super-organized filing cabinets for computer data.",
      "They help us store, find, and manage large amounts of information efficiently.",
      "But different types of information need different kinds of organization!"
    ]
  },
  {
    title: "📊 Slide 2: Traditional SQL Databases - Neat Rows & Columns 📊",
    content: [
      "SQL databases (like our SQLite!) are like spreadsheets on steroids.",
      "They store data in tables with strict rows and columns (e.g., Recipe Name, Rating, Cook Time).",
      "Great for exact matches: \"Find recipes with a 5-star rating.\" ⭐⭐⭐⭐⭐",
      "Keywords: Structured, Tables, Exact Matches."
    ]
  },
  {
    title: "🧠 Slide 3: Vector Databases - Understanding Meaning 🤔",
    content: [
      "Vector databases (like our ChromaDB!) work differently. They understand the *meaning* or *context* of data, not just exact words.",
      "They store data (like text descriptions or ingredients) as numerical representations called \"vectors\" or \"embeddings\". 🔢",
      "Think of it like plotting related concepts close together on a map. 🗺️"
    ]
  },
  {
    title: "✨ Slide 4: How Vector Search Works - Finding \"Similar\" ✨",
    content: [
      "Instead of exact matches, you search for *similarity*.",
      "\"Find recipes *like* chicken soup\" 🍲 or \"What can I make with leftover broccoli and cheese?\" 🥦🧀",
      "The database finds items whose \"vector maps\" are closest to your query's map.",
      "Keywords: Semantic Search, Similarity, Meaning, Embeddings."
    ]
  },
  {
    title: "👍 Slide 5: SQL vs. Vector - When to Use Which? 👍",
    content: [
      "<strong>SQL:</strong> Best for structured data, precise lookups, filtering by specific values (ratings, names, categories). 🗄️➡️🔍",
      "<strong>Vector:</strong> Best for unstructured data (text!), finding things based on meaning, recommendations, semantic search. 🧠➡️💖",
      "Our project uses <strong>both</strong>! SQL for recipe facts, Vector DB for understanding your natural language requests! 🎉"
    ]
  }
];

const ragSlides = [
  {
    title: "✨ Slide 6: The Magic of Understanding Words: Text Embeddings ✨",
    content: [
      "How does a computer understand \"delicious chicken recipes\"? It uses <strong>Text Embeddings</strong>! 🔡➡️🔢",
      "This technique turns words, sentences, or even whole documents into lists of numbers (vectors).",
      "Similar words/concepts get similar number lists (e.g., \"chicken\" and \"poultry\" vectors would be close).",
      "It's like giving words coordinates on that meaning map! 🗺️📍"
    ]
  },
  {
    title: "🤔 Slide 7: Why Embeddings? Searching by Meaning! 💡",
    content: [
      "Remember our Vector Database? It stores these number lists (embeddings).",
      "When you ask, \"What can I make with beef and potatoes?\", we turn *your* question into an embedding too!",
      "Then, we search the database for recipe embeddings that are numerically closest (most similar in meaning).",
      "This finds recipes even if they don't use your *exact* words! Genius! 🤯"
    ]
  },
  {
    title: "📚 Slide 8: Meet RAG: Retrieval-Augmented Generation 🧠➡️✍️",
    content: [
      "Okay, we found relevant recipes using embeddings (that's the <strong>Retrieval</strong> part).",
      "But we don't just dump raw data! We use a smart AI (a Large Language Model or LLM) to craft a nice answer.",
      "<strong>RAG</strong> means the AI *first* looks up relevant info (Retrieval) from our database..."
    ]
  },
  {
    title: "🌱 Slide 9: RAG in Action: Grounded Answers 💬",
    content: [
      "...and *then* uses that retrieved information (<strong>Augmented</strong>) to <strong>Generate</strong> a helpful, factual response.",
      "So, instead of making things up (hallucinating), the AI bases its answer on the actual recipes found.",
      "Example: It finds 3 recipes and says, \"Based on recipes X, Y, and Z, you could make...\"",
      "This makes the assistant much more reliable! ✅"
    ]
  },
  {
    title: "💡 Slide 10: Putting It All Together: Smart Search! 🔍➡️👩‍🍳",
    content: [
      "<strong>You Ask:</strong> \"Healthy dinner ideas with fish?\" 🐟❓",
      "<strong>Embed:</strong> Your question becomes numbers. 🔢",
      "<strong>Retrieve:</strong> Vector DB finds similar healthy fish recipe embeddings. 🎣",
      "<strong>Augment & Generate (RAG):</strong> AI uses the found recipes to create a helpful suggestion list! 📝➡️😋",
      "Result: You get relevant, grounded recipe ideas! 🎉"
    ]
  }
];

const promptSlides = [
  {
    title: "🗣️ Slide 11: Talking to the AI: Prompt Engineering Basics ✍️➡️🤖",
    content: [
      "How do we get the AI (LLM) to do specific tasks, like summarizing a recipe or extracting ingredients? We use <strong>Prompt Engineering</strong>!",
      "A \"prompt\" is simply the instruction we give the AI.",
      "\"Engineering\" means carefully crafting these instructions to get the best possible result. It's like asking a question *just right*."
    ]
  },
  {
    title: "✨ Slide 12: Why Good Prompts Matter 🎯",
    content: [
      "Vague Prompt: \"Tell me about this recipe.\" ➡️ Might get a long, rambling summary.",
      "Good Prompt: \"Extract the ingredients list from this recipe text. Format as a numbered list.\" ➡️ Gets exactly what we need!",
      "Good prompts lead to more accurate, relevant, and useful AI responses. Less guesswork for the AI! 👍"
    ]
  },
  {
    title: "🔍 Slide 13: Example: Recipe Search Prompt 📝",
    content: [
      "When you search, we don't just send your raw query (\"chicken and broccoli ideas\").",
      "We wrap it in a prompt like: \"You are a helpful recipe assistant. Based on the user's query '{user_query}' and the following retrieved recipes: {retrieved_recipes}, suggest 3 relevant recipe titles.\"",
      "This gives the AI context and tells it *exactly* what to do."
    ]
  },
  {
    title: "🍎 Slide 14: Example: Nutrition Summary Prompt 📊",
    content: [
      "To get nutrition info, we might prompt: \"Given the following nutrition data for recipe '{recipe_name}': {nutrition_data}. Summarize the key macronutrients (calories, protein, fat, carbs) in a short sentence.\"",
      "This focuses the AI on the specific task and desired output format."
    ]
  },
  {
    title: "🎨 Slide 15: It's an Art & Science! 🔬",
    content: [
      "Prompt engineering involves:",
      "<ul>",
      "<li>Giving clear instructions.</li>",
      "<li>Providing context (like retrieved data - RAG!).</li>",
      "<li>Specifying the desired output format (list, summary, JSON).</li>",
      "<li>Sometimes, giving examples (few-shot prompting).</li>",
      "</ul>",
      "It's key to making our AI assistant helpful and reliable! 🎉"
    ]
  }
];

const llmCoreSlides = [
  {
    title: "🧠 Slide 21: The Brain of Chefbelle 🧠💡✨",
    content: [
      "The <strong>Large Language Model (LLM)</strong>, powered by Google's <strong>Gemini</strong> architecture, serves as the central intelligence for Chefbelle's language capabilities. 🌟",
      "It receives your processed voice commands, understands their meaning through complex neural networks, and formulates coherent, helpful responses.",
      "Think of it as our AI's brain, performing the sophisticated tasks of <strong>understanding human language</strong>, <strong>reasoning about cooking concepts</strong>, and <strong>generating helpful replies</strong>. 🧠💬"
    ]
  },
  {
    title: "📚 Slide 22: How Gemini's Neural Networks Learn 📖➡️🧠",
    content: [
      "Gemini is trained on <strong>billions of text examples</strong> from books, websites, recipes, cooking blogs, and culinary science! 🍲📊",
      "Through this training, it develops <strong>neural pathways</strong> that recognize patterns in language structure, cooking concepts, and food relationships.",
      "This massive knowledge base allows it to understand context and nuance in your culinary questions about techniques, ingredient substitutions, and meal planning. 🔍👩‍🍳",
      "It's like having a chef, nutritionist, and food scientist who has read virtually every recipe book ever written! 📚✨"
    ]
  },
  {
    title: "🔮 Slide 23: Gemini's Culinary Superpowers ✨🍳",
    content: [
      "<strong>Multimodal Understanding:</strong> Gemini can process both text and images, so it could potentially understand food photos and written recipes! 📝🖼️",
      "<strong>Contextual Intelligence:</strong> It remembers your previous questions in a conversation, creating a more natural interaction. 💬",
      "<strong>Reasoning Abilities:</strong> It can make logical connections between ingredients, techniques, and outcomes. 🧩",
      "<strong>Precision:</strong> It follows specific instructions precisely - perfect for helping with complex recipe modifications! 🎯",
      "These capabilities make Gemini ideal for handling queries like \"What can I make with chicken, spinach and feta that's low-carb?\" 🥗"
    ]
  },
  {
    title: "🔄 Slide 24: Gemini + Kitchen Tech = Culinary Magic! ✨🍽️",
    content: [
      "While Gemini has impressive general cooking knowledge, it might occasionally <strong>hallucinate</strong> (create plausible but incorrect information).",
      "To create a trustworthy kitchen assistant, we enhance Gemini with specialized technologies:",
      "<ul>",
      "<li><strong>Vector DB:</strong> Grounds recommendations in real, tested recipes 📋</li>",
      "<li><strong>SQL DB:</strong> Provides precise nutrition facts and measurements ⚖️</li>",
      "<li><strong>LangGraph:</strong> Creates logical flows for complex cooking assistance 🔄</li>",
      "<li><strong>RAG:</strong> Ensures responses are factual and reliable 📊</li>",
      "</ul>",
      "This creates a kitchen assistant that combines AI creativity with factual reliability! 🧠💪"
    ]
  },
  {
    title: "🧪 Slide 25: Gemini in Action - The Recipe Journey 🎬🍝",
    content: [
      "Let's see what happens when you ask: \"Can I make a healthy pasta with spinach, tomatoes and feta?\" 🥘",
      "<strong>1.</strong> Gemini <em>understands</em> you need a healthy pasta recipe with specific ingredients",
      "<strong>2.</strong> It <em>recognizes</em> this requires recipe search and nutrition knowledge",
      "<strong>3.</strong> It <em>collaborates</em> with RAG to retrieve relevant Mediterranean pasta recipes",
      "<strong>4.</strong> It <em>analyzes</em> nutrition data to confirm the health benefits",
      "<strong>5.</strong> It <em>generates</em> a personalized response with cooking tips, possible variations, and nutritional insights",
      "All this happens in seconds, creating a seamless, intelligent cooking assistant experience! 🚀👨‍🍳"
    ]
  }
];

const audioSlides = [
  {
    title: "🗣️ Slide 26: Hearing You Loud and Clear 🗣️👂",
    content: [
      "This is the very first step in how you interact with Chefbelle using your voice. It involves capturing the audio of your speech and converting it into written words. 🎤",
      "Think of it like Chefbelle's ears, listening to your instructions and questions. This process makes your voice commands accessible for the rest of the system to process. 🗣️👂➡️📄",
      "Without accurate audio understanding, Chefbelle wouldn't know what you're asking it to do. It's the foundational layer for voice-based control in your kitchen."
    ]
  },
  {
    title: "🎤 Slide 27: How Chefbelle Listens 🎤➡️📄",
    content: [
      "Chefbelle relies on sophisticated <strong>Speech-to-Text engines</strong> to perform this conversion. Technologies like OpenAI's Whisper or Google Speech have been trained on massive datasets of human speech. 🔊✨",
      "This training allows them to accurately transcribe a wide variety of accents, languages, and speaking styles. 🌍🗣️",
      "The better the Speech-to-Text engine, the more reliably Chefbelle can understand your voice, even with background noise common in a kitchen environment. 🍳🔊",
      "High accuracy here is key to a smooth user experience! 🎤✨📝"
    ]
  },
  {
    title: "🤔 Slide 28: From Sound to Sense 🎶➡️🔡➡️🧠",
    content: [
      "Once your voice is turned into text, this text isn't immediately ready for Chefbelle to execute a command.",
      "The system needs to <strong>analyze the text</strong> to understand the meaning and intent behind your words. It identifies keywords, phrases, and the overall structure of your request. 🔍",
      "This step transforms the raw text data into structured information that Chefbelle's core intelligence and tools can work with. 📊",
      "It's about making sense of what you've said in a way the system can process effectively. 🎶➡️🔡➡️🧠"
    ]
  },
  {
    title: "🚀 Slide 29: Why Speech-to-Text? Hands-Free Cooking! 🙌🍳",
    content: [
      "The kitchen can be a busy place, and your hands are often occupied with cooking tasks. 👩‍🍳",
      "Speech-to-Text interaction is incredibly valuable because it allows you to control Chefbelle and get information <strong>without needing to touch a screen</strong>. 🙌",
      "You can ask for ingredient measurements, recipe steps, or set timers just by speaking your command aloud. ⏲️🗣️",
      "This hands-free convenience makes Chefbelle a truly practical assistant in a cooking environment, keeping your workflow smooth and your hands clean! 🙌🍳💡"
    ]
  }
];

const genAIEvaluationSlides = [
  {
    title: "📈 Slide 50: Measuring Chefbelle's Performance 📈📊",
    content: [
      "GenAI Evaluation involves assessing the quality and effectiveness of Chefbelle's generated responses and actions. 📈📊✅",
      "It's a systematic process that ensures Chefbelle delivers value by measuring how well it understands and addresses cooking queries.",
      "Rather than simply assuming Chefbelle is helpful, we use specific metrics and tests to quantify its performance in real-world cooking scenarios."
    ]
  },
  {
    title: "🧠 Slide 51: Focus on Reasoned Responses 🧠✅",
    content: [
      "GenAI Evaluation prioritizes answers that are logical, well-supported by data, and directly address the user's query, rather than just generating text. 🧠💡🎯",
      "For example, when suggesting substitutions for ingredients, Chefbelle must provide accurate, chemistry-based explanations of why certain substitutes work.",
      "This approach ensures that Chefbelle's cooking advice is reliable and based on culinary principles rather than simply sounding plausible."
    ]
  },
  {
    title: "📉 Slide 52: Data-Driven Improvement 📉⬆️",
    content: [
      "The evaluation process uses results to identify areas for improvement and refine Chefbelle's performance and capabilities. 📊➡️✨",
      "We analyze patterns in questions that Chefbelle struggles with, such as complex recipe modifications or specialized dietary queries.",
      "This data guides ongoing development, helping us focus on enhancing specific capabilities like nutrition calculations or ingredient substitution recommendations."
    ]
  },
  {
    title: "✨ Slide 53: Quality Over Quantity ✨👍",
    content: [
      "GenAI Evaluation emphasizes the accuracy, relevance, and helpfulness of the generated output rather than how much Chefbelle can say. ✨👍💯",
      "A concise, correct answer about cooking times is more valuable than a lengthy but incorrect explanation.",
      "We specifically test for factual accuracy in areas like food safety, nutrition facts, and cooking techniques where precision matters most."
    ]
  },
  {
    title: "💡 Slide 54: Smarter, More Useful Answers 💡🎯",
    content: [
      "The ultimate goal of GenAI Evaluation is to ensure Chefbelle provides genuinely helpful, reliable, and intelligent assistance for your cooking needs. 💡👩‍🍳👨‍🍳",
      "Through continuous testing and refinement, Chefbelle becomes increasingly capable of solving real kitchen challenges.",
      "This commitment to evaluation means your kitchen assistant keeps getting better over time, adapting to new cooking trends and incorporating user feedback.",
      "The result is a continuously improving cooking companion that truly helps you create better meals!"
    ]
  }
];

const statefulConversationSlides = [
  {
    title: "💾 Slide 45: Chefbelle Remembers! 🤔💾",
    content: [
      "Stateful Conversation gives Chefbelle the ability to recall previous turns in your conversation. 🧠💾⏪",
      "Unlike a basic chatbot that treats each question as isolated, Chefbelle maintains a memory of your entire interaction.",
      "This means you can have a flowing conversation about recipes, ingredients, or cooking techniques without starting from scratch each time."
    ]
  },
  {
    title: "➡️ Slide 46: Continuous Dialogue Flow ➡️➡️➡️",
    content: [
      "Stateful Conversation makes the interaction feel natural and seamless, allowing you to refer back to earlier parts of the discussion. 🗣️🔄💬",
      "For example, if you discussed pasta recipes earlier, you can simply ask \"How about a vegetarian version?\" and Chefbelle knows you're still talking about pasta.",
      "This creates a more cohesive, human-like conversation experience in your kitchen."
    ]
  },
  {
    title: "📖 Slide 47: Understanding Context 📖💡",
    content: [
      "Knowing the conversation history helps Chefbelle interpret your current requests accurately, even if they are brief or referential. 📚💡🤔",
      "When you say \"How long does it take to cook?\", Chefbelle knows which recipe you're referring to from earlier in the conversation.",
      "This contextual understanding makes your interactions more efficient and intuitive, especially when exploring complex cooking topics."
    ]
  },
  {
    title: "🧱 Slide 48: Building on Previous Turns 🧱⬆️",
    content: [
      "Chefbelle can pick up where you left off, allowing for follow-up questions or continuing a task without repeating yourself. ⬆️🏗️💬",
      "If you've already specified dietary restrictions earlier in the conversation, Chefbelle remembers these preferences in all subsequent recipe suggestions.",
      "This progressive conversation style lets you refine and explore cooking ideas more naturally, just like talking with a real chef."
    ]
  },
  {
    title: "🤗 Slide 49: A More Natural Chat 🤗🗣️",
    content: [
      "Stateful Conversation provides a user experience closer to talking with a human assistant who remembers the conversation history. 🤗👩‍🍳👨‍🍳",
      "You don't need to awkwardly repeat context or remind Chefbelle what you're cooking - just continue the conversation naturally.",
      "This creates a more helpful, personalized, and enjoyable cooking companion that feels more like a knowledgeable friend in your kitchen than a simple tool."
    ]
  }
];

const structuredOutputSlides = [
  {
    title: "⚙️ Slide 40: Organized Information Flow ⚙️📦",
    content: [
      "Structured Output ensures that data exchanged between Chefbelle and its tools is neatly organized and easy for machines to process. ⚙️📊📦",
      "Think of it like having a standardized recipe card format - everything has its specific place, making information clear and consistent.",
      "This organization is crucial for Chefbelle to reliably process information about recipes, ingredients, and cooking techniques."
    ]
  },
  {
    title: "🤝 Slide 41: Speaking the Same Language 🤝💬",
    content: [
      "Chefbelle uses JSON (JavaScript Object Notation) as a universal format for reliable communication between different parts of the system. 🤝💾🗣️",
      "JSON acts like a common language that all components of the system understand, whether it's the database, search tools, or the AI itself.",
      "This consistency ensures that when Chefbelle asks for recipe information, it receives data in exactly the format it expects."
    ]
  },
  {
    title: "🤔 Slide 42: Why JSON? 🤔",
    content: [
      "JSON is a lightweight and human-readable format that is also easy for computers to parse and generate, ensuring efficient data transfer. 👍💻",
      "It's flexible enough to represent complex data like recipe ingredients, cooking times, nutrition information, and user preferences.",
      "Looks like <strong>{\"title\": \"Pasta Primavera\", \"cookTime\": 30, \"ingredients\": [\"pasta\", \"vegetables\"]}</strong> - clean structure that both humans and machines understand."
    ]
  },
  {
    title: "🛠️ Slide 43: Data for Tools & Responses 🛠️💬",
    content: [
      "Structured Output is used to send information to tools (like search queries or database requests) and receive structured results back for generating responses. ➡️🛠️⬅️💬",
      "When you ask about nutrition facts, Chefbelle sends a structured query to the nutrition database and gets back organized data about calories, proteins, etc.",
      "This structured approach helps Chefbelle know exactly where to find specific information in the responses from its tools."
    ]
  },
  {
    title: "💪 Slide 44: Reliable Data Exchange 💪📨",
    content: [
      "Structured Output minimizes errors and ensures that the data Chefbelle receives from its tools is accurate and ready to be used to formulate your answer. 💪📨✨",
      "It creates a systematic pathway for information flow, reducing the chances of misinterpreting data or losing important details.",
      "For example, when listing ingredients, using structured data ensures exact quantities, units, and preparation methods are preserved.",
      "The result is more precise and reliable cooking assistance that you can count on in the kitchen!"
    ]
  }
];

const groundingSlides = [
  {
    title: "✅ Slide 35: Staying Updated & Accurate ✅",
    content: [
      "Grounding ensures Chefbelle provides reliable, current, and contextually relevant answers to your cooking questions. ✅📰✨",
      "It's the difference between answers based only on training data versus answers enriched with up-to-date information.",
      "Think of it as Chefbelle's way of double-checking facts before giving you advice about recipes, ingredients, or techniques."
    ]
  },
  {
    title: "🌐 Slide 36: Chefbelle's Fact-Check 🌐🔍",
    content: [
      "Chefbelle uses tools like Google Search to verify information and get real-time data on topics outside its core knowledge. 🌐🔍🤔",
      "When you ask about a new cooking trend or seasonal ingredient, Chefbelle can check for the most current information rather than relying on older data.",
      "This helps ensure that advice about ingredient substitutions, cooking methods, or dietary claims is accurate and up-to-date."
    ]
  },
  {
    title: "⏰ Slide 37: Real-Time Answers ⏰💡",
    content: [
      "Grounding allows Chefbelle to provide up-to-the-minute information on things like ingredient availability, recipe variations, or general cooking tips. ⏰✅🍳",
      "For example, if you ask about using a trendy new ingredient, Chefbelle can find current information about how chefs are using it.",
      "This ensures that the cooking assistance you receive reflects current culinary practices and food safety recommendations."
    ]
  },
  {
    title: "🧠 Slide 38: Beyond Stored Knowledge 🧠➡️🌍",
    content: [
      "Grounding connects Chefbelle to the vast information available on the internet, expanding its ability to answer diverse queries. 🧠➡️🌍📚",
      "While Chefbelle has extensive built-in knowledge about cooking, grounding extends this to specialized cuisines, regional variations, and emerging food trends.",
      "This combination of deep culinary understanding with up-to-date information makes Chefbelle a comprehensive cooking resource."
    ]
  },
  {
    title: "👍 Slide 39: Trustworthy Cooking Advice 👍🍳",
    content: [
      "Grounding provides confidence in the information Chefbelle gives you, as it's based on current and verifiable data. 👍🧑‍🍳👩‍🍳",
      "When Chefbelle answers questions about food safety, nutrition facts, or ingredient properties, you can trust that the information is reliable.",
      "This is especially important in cooking, where outdated or incorrect information could affect meal quality, dietary needs, or even food safety!",
      "The result is a kitchen assistant you can truly rely on for all your culinary questions and challenges."
    ]
  }
];

const functionCallingSlides = [
  {
    title: "🔧 Slide 30: Chefbelle's Toolbelt 🔧🛠️",
    content: [
      "Function Calling allows Chefbelle to go beyond just talking and actually interact with external systems and data sources. 🛠️🔗",
      "It's like giving Chefbelle the ability to use specialized tools when conversation alone isn't enough to help you.",
      "This capability transforms Chefbelle from a simple chatbot into a powerful kitchen assistant that can take action on your behalf!"
    ]
  },
  {
    title: "📊 Slide 31: Talking to Databases 📊",
    content: [
      "Function Calling enables Chefbelle to retrieve specific information from structured data sources, like nutritional facts from the USDA database. 📊🔍🥑",
      "When you ask \"How many calories are in an avocado?\", Chefbelle doesn't guess - it uses Function Calling to query a reliable nutrition database.",
      "This ensures you get accurate, up-to-date information rather than potentially outdated or incorrect answers."
    ]
  },
  {
    title: "🔌 Slide 32: Using APIs 🔌",
    content: [
      "Function Calling connects Chefbelle with Application Programming Interfaces (APIs) of other services to access real-time data or perform specific actions. 🔌🌐📬",
      "For example, it might check current seasonal ingredients, popular recipe trends, or even specialized dietary information.",
      "These connections extend Chefbelle's capabilities far beyond what it could do on its own, creating a more helpful assistant."
    ]
  },
  {
    title: "🧐 Slide 33: Getting Specific Info 🧐",
    content: [
      "Function Calling is used to fetch precise details needed to answer your questions accurately, such as ingredient properties or cooking times from reliable sources. 🤔➡️📈",
      "When you ask about substituting ingredients or scaling a recipe, Chefbelle can call functions to perform calculations or lookups for the most accurate advice.",
      "This precision is especially important in cooking, where small changes can significantly impact results!"
    ]
  },
  {
    title: "💪 Slide 34: Action & Information! 💪💡",
    content: [
      "Function Calling is the mechanism that empowers Chefbelle to fetch relevant information and potentially trigger actions based on your requests. 💪💡✨",
      "It works behind the scenes to recognize when specialized tools are needed and how to use them properly.",
      "The process is seamless to you - you simply ask a question, and Chefbelle determines whether to answer directly or call a function to help provide the best response.",
      "This makes your interaction with Chefbelle feel natural while still delivering powerful, accurate assistance!"
    ]
  }
];

const langGraphSlides = [
  {
    title: "🧑‍🤝‍🧑 Slide 16: Meet LangGraph - The AI Team Coordinator! 🧠🎶",
    content: [
      "Imagine our AI assistant has different skills (search, summarize, calculate nutrition). LangGraph is like the <strong>team coordinator</strong> or <strong>conductor</strong>.",
      "It decides *which skill* (or \"tool\") to use when, based on your request and the current situation.",
      "It manages the flow of information between different AI steps."
    ]
  },
  {
    title: "🔄 Slide 17: Why LangGraph? Handling Complex Tasks 🧩",
    content: [
      "Simple tasks might be one step (Ask -> Answer).",
      "But what if the AI needs to search, *then* summarize, *then* check nutrition? That's multiple steps!",
      "LangGraph allows the AI to loop, make decisions, and call different tools in a sequence until the task is done. It handles <strong>cycles</strong> and <strong>state</strong>."
    ]
  },
  {
    title: "📊 Slide 18: Nodes and Edges - The Flowchart ➡️🧠",
    content: [
      "LangGraph thinks in terms of <strong>Nodes</strong> (steps or actions, like \"call search tool\") and <strong>Edges</strong> (connections showing the next possible step).",
      "It's like a flowchart for the AI's thought process.",
      "An \"edge\" might say: \"If the search found results, go to the 'summarize' node. If not, ask the user for clarification.\""
    ]
  },
  {
    title: "📝 Slide 19: State - Remembering What's Happening 🤔",
    content: [
      "LangGraph maintains a \"state\" - it remembers what's happened so far in the conversation.",
      "This includes your original request, any search results found, calculations made, etc.",
      "Each node can read from and add to the state, passing information along the chain."
    ]
  },
  {
    title: "🗣️ Slide 20: Our Assistant's Flow ➡️🔍➡️✍️➡️✅",
    content: [
      "<strong>You Ask:</strong> LangGraph gets the input.",
      "<strong>Decide:</strong> Which tool is needed? (SQL search? Vector search? Nutrition lookup?) -> Node",
      "<strong>Execute:</strong> Run the chosen tool. -> Node",
      "<strong>Update State:</strong> Store the results.",
      "<strong>Loop/Finish:</strong> Does it need another tool? Or is it ready to generate the final answer? -> Edge -> Node",
      "LangGraph orchestrates this whole dance! 💃🕺"
    ]
  }
];

// Responsive settings for the carousel (FROM TechDeepDive_multi_crousel.js)
const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 4
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1
  }
};

// Section metadata for the carousel (FROM TechDeepDive_multi_crousel.js - Replacing original)
const techSections = [
  {
    id: 'audio',
    title: "Audio Understanding",
    icon: <AudioIcon />,
    description: "See how Chefbelle converts your voice commands into actionable instructions",
    slides: audioSlides,
    color: '#E6FFFA' // Light teal background
  },
  {
    id: 'db',
    title: "Databases",
    icon: <DatabaseIcon />,
    description: "Learn about SQL vs Vector databases and how they power our recipe search",
    slides: dbSlides,
    color: '#FFE4E1' // Light pink background
  },
  {
    id: 'rag',
    title: "RAG & Embeddings",
    icon: <RagEmbeddingsIcon />,
    description: "Discover how we understand meaning with embeddings and ground answers in facts",
    slides: ragSlides,
    color: '#E6E6FA' // Light lavender background
  },
  {
    id: 'llmcore',
    title: "LLM Core (Gemini)",
    icon: <LLMCoreIcon />,
    description: "Explore how Gemini powers natural language understanding and response generation",
    slides: llmCoreSlides,
    color: '#FFF5E1' // Light golden background
  },
  {
    id: 'grounding',
    title: "Grounding",
    icon: <GroundingIcon />,
    description: "Learn how Chefbelle provides reliable, current, and factual cooking information",
    slides: groundingSlides,
    color: '#E6F2FF' // Light blue background
  },
  {
    id: 'structured',
    title: "Structured Output",
    icon: <StructuredOutputIcon />,
    description: "Learn how JSON formatting enables efficient data exchange between system components",
    slides: structuredOutputSlides,
    color: '#F5F5DC' // Beige background
  },
  {
    id: 'stateful',
    title: "Stateful Conversation",
    icon: <StatefulConversationIcon />,
    description: "Discover how Chefbelle remembers your conversation to provide contextual assistance",
    slides: statefulConversationSlides,
    color: '#FFF0D0' // Light peach background
  },
  {
    id: 'prompt',
    title: "Prompt Engineering",
    icon: <PromptEngineeringIcon />,
    description: "See how we craft perfect instructions to get the best AI responses",
    slides: promptSlides,
    color: '#E0FFFF' // Light cyan background
  },
  {
    id: 'langgraph',
    title: "LangGraph",
    icon: <LangGraphIcon />,
    description: "Explore how we coordinate complex AI tasks with decision flows",
    slides: langGraphSlides,
    color: '#F0FFF0' // Light mint background
  },
  {
    id: 'function',
    title: "Function Calling",
    icon: <FunctionCallingIcon />,
    description: "Learn how Chefbelle interacts with external systems to enhance its capabilities",
    slides: functionCallingSlides,
    color: '#FFF0F5' // Lavender blush background
  },
  {
    id: 'evaluation',
    title: "GenAI Evaluation",
    icon: <GenAIEvaluationIcon />,
    description: "Learn how we measure and improve Chefbelle's performance for better cooking assistance",
    slides: genAIEvaluationSlides,
    color: '#F8F8FF' // Ghost white background
  }
];

// --- Theme UI theme for blog styling (Keep this unchanged) ---
const theme = {
  colors: {
    background: '#fff',
    text: '#333',
    primary: '#6459CF',
    secondary: '#FF6B6B',
    muted: '#f6f6f6',
    highlight: '#E6F7FF',
    gray: '#DDDDDF',
    accent: '#FFD166',
    darken: '#00044C',
  },
  fonts: {
    body: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    heading: 'Georgia, serif',
    monospace: 'Menlo, monospace',
  },
  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 96],
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
  radii: {
    default: 4,
    circle: 99999,
  },
  shadows: {
    card: '0 0 4px rgba(0, 0, 0, .125)',
    small: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    large: '0 8px 16px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.24)',
  },
  variants: {
    card: {
      p: 4,
      borderRadius: 'default',
      boxShadow: 'card',
      bg: 'white',
      transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
      '&:hover': {
        boxShadow: 'large',
      }
    },
    badge: {
      display: 'inline-block',
      px: 2,
      py: 1,
      borderRadius: 'default',
      color: 'white',
      bg: 'primary',
      fontSize: 0,
    },
    phaseBadge: {
      display: 'inline-block',
      px: 3,
      py: 1,
      borderRadius: 'default',
      color: 'white',
      bg: 'primary',
      fontSize: 1,
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    }
  }
};

// --- CodeBlock component for syntax highlighting (Keep this unchanged) ---
const CodeBlock = ({ language, code, fileName }) => {
  return (
    <Box sx={{ position: 'relative', my: 4, borderRadius: 'default', overflow: 'hidden' }}>
      {fileName && (
        <Box sx={{
          bg: 'darken',
          color: 'white',
          py: 1,
          px: 3,
          fontSize: 1,
          fontFamily: 'monospace',
          borderTopLeftRadius: 'default',
          borderTopRightRadius: 'default'
        }}>
          {fileName}
        </Box>
      )}
      <SyntaxHighlighter
        language={language}
        style={docco}
        customStyle={{
          margin: 0,
          borderRadius: fileName ? '0 0 4px 4px' : '4px',
          fontSize: '14px',
        }}
      >
        {code}
      </SyntaxHighlighter>
    </Box>
  );
};

// --- Technical blog content - simplified version with string literals (Keep this unchanged) ---
const technicalBlogSections = [
  // Phase 1: Setting the Stage
  {
    phase: 1,
    title: "Setting the Stage: Preparing Our Workspace",
    emoji: "🛠️",
    content: "Before diving into development, we needed to establish a robust environment for our AI kitchen assistant. This involved setting up a development workspace with the necessary libraries, frameworks, and tools that would power Chefbelle. We created a project structure that would support both local development and future deployment, ensuring scalability and maintainability from the start.",
    code: {
      language: "python",
      fileName: "environment_setup.py",
      snippet: `# Essential imports for our AI kitchen assistant
import pandas as pd
import numpy as np
import sqlite3
import chromadb
import json
import os
import re
from dotenv import load_dotenv

# For language model and embeddings
import google.generativeai as genai
from langchain.text_splitter import RecursiveCharacterTextSplitter
from langchain.embeddings import VertexAIEmbeddings
from langchain_google_genai import GoogleGenerativeAIEmbeddings

# For LangGraph agent framework
from langchain_core.messages import HumanMessage, AIMessage
from langgraph.graph import StateGraph, END
from langchain_core.runnables import RunnableLambda, RunnablePassthrough`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase1_project_structure.svg").default,
        alt: "Project structure setup",
        caption: "Initial project structure and organization",
        filename: "phase1_project_structure.svg"
      },
      {
        src: require("../../assets/images/tech/phase1_environment_setup.png"),
        alt: "Environment configuration",
        caption: "Development environment and dependencies",
        filename: "phase1_environment_setup.png"
      }
    ]
  },
  
  // Phase 2: Data Foundation (formerly Phase 1)
  {
    phase: 2,
    title: "Gathering & Tasting the Ingredients",
    emoji: "📊",
    content: "Every great recipe starts with quality ingredients. For Chefbelle, our 'ingredients' are data. We started with publicly available datasets: Food.com Recipes & Interactions from Kaggle, providing ~230,000 recipes with detailed ingredients, steps, descriptions, tags, and over 1.1 million user ratings/reviews. We also added a nutritional database to enhance our knowledge base.",
    code: {
      language: "python",
      fileName: "data_loading.py",
      snippet: `# Load the core datasets
recipes_df = pd.read_csv('/kaggle/input/food-com-recipes-and-user-interactions/RAW_recipes.csv')
interactions_df = pd.read_csv('/kaggle/input/food-com-recipes-and-user-interactions/RAW_interactions.csv')
# Load supplementary nutrition data
nutrition_df = pd.read_csv('/kaggle/input/nutritional-breakdown-of-foods/cleaned_nutrition_dataset.csv')

print(f"Loaded {len(recipes_df)} recipes and {len(interactions_df)} interactions.")`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase1_data_loading.svg").default,
        alt: "Data loading process",
        caption: "Loading process for our three main datasets",
        filename: "phase1_data_loading.svg"
      },
      {
        src: require("../../assets/images/tech/phase1_data_sources.png"),
        alt: "Data sources visualization",
        caption: "Visual representation of our data source integration",
        filename: "phase1_data_sources.png"
      }
    ]
  },

  // Phase 3: Data Exploration (formerly Phase 2)
  {
    phase: 3,
    title: "Data Exploration & Analysis",
    emoji: "🔍",
    content: "Before processing, we needed to understand the data's shape and quirks. We examined column types, missing values, and common patterns. Key insights included: critical fields like ingredients and steps were stored as string representations of Python lists, the tags column was a rich source for deriving features like cuisine_type, and minimal missing data in essential columns simplified cleaning.",
    code: {
      language: "python",
      fileName: "data_exploration.py",
      snippet: `# Basic inspection
print("Recipe Columns & Types:")
recipes_df.info()

print("\\nMissing Values (Recipes):")
print(recipes_df.isnull().sum())

# Example: Checking common cuisines
print("\\nTop 10 Cuisine Tags (from 'tags' column):")
# Assuming 'tags' column is processed into lists
# flat_tags = [tag for sublist in recipes_df['tags'].dropna() for tag in sublist]
# print(pd.Series(flat_tags).value_counts().head(10))`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase2_data_structure.svg").default,
        alt: "Initial data structure",
        caption: "Structure of the raw recipe data",
        filename: "phase2_data_structure.svg"
      },
      {
        src: require("../../assets/images/tech/phase2_data_insights.png"),
        alt: "Data insights dashboard",
        caption: "Key insights from initial data analysis",
        filename: "phase2_data_insights.png"
      }
    ]
  },

  // Phase 4: Data Cleaning
  {
    phase: 4,
    title: "Data Cleaning & Preprocessing",
    emoji: "✨",
    content: "Raw data is messy. We implemented several cleaning steps: deduplication to ensure recipe uniqueness, parsing stringified lists into actual Python lists for ingredients and steps, ingredient normalization using regex to remove quantities and standardize text, and feature engineering to derive useful attributes like cuisine_type and dietary_tags.",
    code: {
      language: "python",
      fileName: "normalize_ingredients.py",
      snippet: `import re

def normalize_ingredients(ingredient_list):
    normalized = []
    if isinstance(ingredient_list, list):
        for ingredient in ingredient_list:
            # Remove quantities (simplified example)
            cleaned = re.sub(r'^[\\d./\\s]+', '', str(ingredient))
            # Lowercase and strip
            cleaned = cleaned.lower().strip()
            if cleaned:
                normalized.append(cleaned)
    return normalized

recipes_df['normalized_ingredients'] = recipes_df['ingredients'].apply(normalize_ingredients)`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase3_before_after_cleaning.svg").default,
        alt: "Before and after data cleaning",
        caption: "Comparison of raw vs. normalized ingredients",
        filename: "phase3_before_after_cleaning.svg"
      },
      {
        src: require("../../assets/images/tech/phase3_cleaning_pipeline.png"),
        alt: "Data cleaning pipeline",
        caption: "Step-by-step data cleaning workflow",
        filename: "phase3_cleaning_pipeline.png"
      }
    ]
  },

  // Phase 5: Database Setup
  {
    phase: 5,
    title: "Database Setup",
    emoji: "💾",
    content: "Chefbelle needs efficient ways to store and retrieve both structured details and semantic meaning. We implemented a dual-database approach: SQLite for structured, relational data allowing precise queries, and ChromaDB for semantic search capability. This combination provides both exact lookups (SQL) and \"fuzzy\" meaning-based retrieval (Vector DB).",
    code: {
      language: "python",
      fileName: "database_setup.py",
      snippet: `# Inside setup_sql_database function
conn = sqlite3.connect(db_path)
# Preprocess df to ensure SQL compatibility
preprocessed_recipes_df = preprocess_dataframe(recipes_df.copy())
preprocessed_recipes_df.to_sql('recipes', conn, if_exists='replace', index=False)
conn.close()

# Vector database setup
client = chromadb.PersistentClient(path=VECTOR_DB_PATH)
recipe_collection = client.get_or_create_collection(name="recipes")
# Add documents with recipe embeddings and metadata
recipe_collection.add(
    documents=recipe_documents,
    metadatas=recipe_metadatas,
    ids=recipe_ids
)`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase4_database_architecture.svg").default,
        alt: "Database architecture",
        caption: "Architecture of dual SQL and Vector databases",
        filename: "phase4_database_architecture.svg"
      },
      {
        src: require("../../assets/images/tech/phase4_database_schema.png"),
        alt: "Database schema design",
        caption: "Schema design for recipe and nutrition data",
        filename: "phase4_database_schema.png"
      }
    ]
  },

  // Phase 6: Semantic Search
  {
    phase: 6,
    title: "Semantic Search Implementation",
    emoji: "🔍",
    content: "We implemented semantic search capability via the gemini_recipe_similarity_search tool, which wraps ChromaDB query logic. It takes natural language input, converts it to an embedding vector, and searches for similar vectors in the recipes collection, with optional filtering by cuisine, cooking time, and dietary preferences.",
    code: {
      language: "python",
      fileName: "semantic_search.py",
      snippet: `# Inside gemini_recipe_similarity_search tool
filter_dict = {}
if cuisine: filter_dict["cuisine_type"] = cuisine
if dietary_tag: filter_dict["dietary_tags"] = {"$in": [dietary_tag]}
# ... build where_clause ...

results = recipe_collection.query(
    query_texts=[query_text], # Text to be embedded and searched
    n_results=int(n_results),
    where=where_clause,       # Apply metadata filters
    include=["metadatas", "distances"] # Get metadata and similarity score
)
# Process results into JSON output...`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase5_vector_search_viz.svg").default,
        alt: "Vector search visualization",
        caption: "Visualization of semantic similarity search",
        filename: "phase5_vector_search_viz.svg"
      },
      {
        src: require("../../assets/images/tech/phase5_semantic_search_results.png"),
        alt: "Semantic search results",
        caption: "Examples of semantic search with different queries",
        filename: "phase5_semantic_search_results.png"
      }
    ]
  },

  // Phase 7: Audio Input
  {
    phase: 7,
    title: "Audio Input Processing",
    emoji: "🎤",
    content: "Enabling hands-free interaction is crucial in a kitchen environment. We implemented the transcribe_audio function that supports both Google Cloud Speech-to-Text and OpenAI Whisper, handles authentication, and converts voice commands from audio files into text for the LangGraph agent to process.",
    code: {
      language: "python",
      fileName: "audio_transcription.py",
      snippet: `# Snippet for Google Cloud Speech within transcribe_audio
from google.cloud import speech
# ... credential handling ...
client = speech.SpeechClient()
with io.open(file_path, "rb") as audio_file:
    content = audio_file.read()
audio = speech.RecognitionAudio(content=content)
config = speech.RecognitionConfig(
    encoding=speech.RecognitionConfig.AudioEncoding.OGG_OPUS,
    sample_rate_hertz=48000,
    language_code="en-US",
)
response = client.recognize(config=config, audio=audio)
transcript = response.results[0].alternatives[0].transcript if response.results else ""`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase6_audio_transcription_flow.svg").default,
        alt: "Audio transcription flow",
        caption: "Flow diagram of audio processing pipeline",
        filename: "phase6_audio_transcription_flow.svg"
      },
      {
        src: require("../../assets/images/tech/phase6_voice_command_performance.png"),
        alt: "Voice command performance",
        caption: "Accuracy comparison of different speech-to-text engines",
        filename: "phase6_voice_command_performance.png"
      }
    ]
  },

  // Phase 8: Agent Tools
  {
    phase: 8,
    title: "Agent Tools Development",
    emoji: "🛠️",
    content: "The agent needs specific capabilities beyond text generation. We defined specialized Python functions as tools: get_recipe_by_id fetches structured data from SQLite, gemini_recipe_similarity_search performs semantic search, get_ratings_and_reviews retrieves user feedback, and fetch_nutrition_from_usda_fdc provides reliable nutrition data via an external API.",
    code: {
      language: "python",
      fileName: "nutrition_api.py",
      snippet: `# Snippet from fetch_nutrition_from_usda_fdc
search_url = "https://api.nal.usda.gov/fdc/v1/foods/search"
params = {"query": ingredient_name, "api_key": api_key, ...}
# ... retry loop ...
response = requests.get(search_url, params=params, ...)
data = response.json()
# ... extract nutrients using FDC_NUTRIENT_MAP ...
return json.dumps(filtered_nutrition, indent=2)`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase7_tool_architecture.svg").default,
        alt: "Tool architecture",
        caption: "Architecture of agent tools and API connections",
        filename: "phase7_tool_architecture.svg"
      },
      {
        src: require("../../assets/images/tech/phase7_api_integration_flow.png"),
        alt: "API integration flow",
        caption: "Flow diagram of external API integrations",
        filename: "phase7_api_integration_flow.png"
      }
    ]
  },

  // Phase 9: LangGraph Agent
  {
    phase: 9,
    title: "LangGraph Agent Development",
    emoji: "🧠",
    content: "We used LangGraph to build a stateful conversational agent. Key components include: State (KitchenState) defining memory fields for conversation history and context, Nodes implementing core logic as Python functions, Edges & Routing connecting nodes and implementing decision logic, and LLM Integration using Google's Gemini model with tools bound for intelligent decision-making.",
    code: {
      language: "python",
      fileName: "langraph_agent.py",
      snippet: `# Example conditional edge definition
graph_builder.add_conditional_edges(
    "InputParserNode", # Source node
    route_after_parsing, # Function to determine the route
    { # Mapping from route function output to destination node
        "ToolExecutorNode": "ToolExecutorNode",
        "AggregateNutritionNode": "AggregateNutritionNode",
        "ReviewDashboardNode": "ReviewDashboardNode",
        "ResponseFormatterNode": "ResponseFormatterNode",
        END: END
    }
)`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase8_agent_decision_tree.svg").default,
        alt: "LangGraph flow diagram",
        caption: "Visual representation of the LangGraph agent flow",
        filename: "phase8_agent_decision_tree.svg"
      },
      {
        src: require("../../assets/images/tech/phase8_langraph_flow.svg").default,
        alt: "Agent decision tree",
        caption: "Decision tree for the LangGraph agent's routing logic",
        filename: "phase8_langraph_flow.svg"
      }
    ]
  },

  // Phase 10: Testing
  {
    phase: 10,
    title: "Testing & Interaction",
    emoji: "💬",
    content: "We validated the end-to-end functionality through systematic testing with a chat_with_assistant helper function and UI simulation with ipywidgets. Test scenarios included: initial greeting, recipe search, getting details, reviews, nutrition information, and customization requests, all helping to refine prompts and ensure consistent behavior.",
    code: {
      language: "python",
      fileName: "test_conversation.py",
      snippet: `# Test sequence of interactions
state = {}
state = chat_with_assistant("Hello!", state)
state = chat_with_assistant("I'm looking for healthy chicken recipes that take less than 30 minutes.", state)
state = chat_with_assistant("Tell me about the second recipe.", state)
state = chat_with_assistant("What do people think of this recipe?", state)
state = chat_with_assistant("Can you analyze the nutrition for me?", state)`
    },
    images: [
      {
        src: require("../../assets/images/tech/phase9_conversation_flow.svg").default,
        alt: "Conversation flow",
        caption: "Example conversation flow with Chefbelle",
        filename: "phase9_conversation_flow.svg"
      },
      {
        src: require("../../assets/images/tech/phase9_test_results.png"),
        alt: "Test results dashboard",
        caption: "Performance metrics from user testing scenarios",
        filename: "phase9_test_results.png"
      }
    ]
  },


];

// --- TechnicalBlog component (Keep this unchanged) ---
const TechnicalBlog = () => {
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const togglePhase = (phaseNumber) => {
    if (expandedPhase === phaseNumber) {
      setExpandedPhase(null);
    } else {
      setExpandedPhase(phaseNumber);
    }
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: [3, 4], pt: 5, pb: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Heading as="h1" sx={{ fontSize: [5, 6], color: 'primary' }}>
            Chefbelle: A Technical Deep Dive 🍳✨
          </Heading>
          <Text sx={{ fontSize: [2, 3], maxWidth: '800px', mx: 'auto', mt: 3 }}>
            Explore the technical journey of crafting an AI kitchen assistant using Generative AI,
            vector databases, and LangGraph. From raw data to intelligent conversation.
          </Text>
        </Box>

        {/* Project Overview */}
        <Card variant="card" sx={{ mb: 5, p: 4 }}>
          <Heading as="h2" sx={{ mb: 3, borderBottom: '2px solid', borderColor: 'muted', pb: 2 }}>
            Project Overview
          </Heading>
          <Text sx={{ fontSize: [1, 2] }}>
            We've all faced the "fridge stare" – a random collection of ingredients, dwindling time, and the looming question:
            "What can I possibly make?" This common kitchen conundrum often leads to food waste, mealtime stress, and reliance on takeout.
            Standard recipe apps, with their static lists and assumption of a fully stocked pantry, rarely bridge this gap.
          </Text>
          <Text sx={{ fontSize: [1, 2], mt: 3 }}>
            This is the problem <strong>Chefbelle</strong> was designed to solve. She's not just a digital cookbook;
            she's an intelligent, conversational AI kitchen assistant built with cutting-edge Generative AI.
            Chefbelle understands your available ingredients, dietary goals, and even helps you decipher nutritional information,
            aiming to make cooking intuitive, less wasteful, and truly personalized.
          </Text>
          <Flex sx={{ mt: 4, flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
            <Badge sx={{ bg: '#6459CF', px: 3, py: 2, fontSize: 1 }}>GenAI</Badge>
            <Badge sx={{ bg: '#FF6B6B', px: 3, py: 2, fontSize: 1 }}>RAG</Badge>
            <Badge sx={{ bg: '#4CAF50', px: 3, py: 2, fontSize: 1 }}>LLM Core (Gemini)</Badge>
            <Badge sx={{ bg: '#03A9F4', px: 3, py: 2, fontSize: 1 }}>Audio Understanding</Badge>
            <Badge sx={{ bg: '#FF9800', px: 3, py: 2, fontSize: 1 }}>Function Calling</Badge>
            <Badge sx={{ bg: '#9C27B0', px: 3, py: 2, fontSize: 1 }}>LangGraph</Badge>
            <Badge sx={{ bg: '#2196F3', px: 3, py: 2, fontSize: 1 }}>Grounding</Badge>
            <Badge sx={{ bg: '#607D8B', px: 3, py: 2, fontSize: 1 }}>Embeddings & Vector DB</Badge>
            <Badge sx={{ bg: '#8BC34A', px: 3, py: 2, fontSize: 1 }}>RAG</Badge>
            <Badge sx={{ bg: '#3F51B5', px: 3, py: 2, fontSize: 1 }}>Structured Output</Badge>
            <Badge sx={{ bg: '#795548', px: 3, py: 2, fontSize: 1 }}>Stateful Conversation</Badge>
            <Badge sx={{ bg: '#E91E63', px: 3, py: 2, fontSize: 1 }}>GenAI Evaluation</Badge>
          </Flex>
        </Card>

        {/* Architecture Diagram */}
        <Box sx={{ my: 5, textAlign: 'center' }}>
          <Heading as="h2" sx={{ mb: 4 }}>System Architecture</Heading>
          <Card 
            variant="card" 
            sx={{ 
              p: 3, 
              bg: 'highlight', 
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer'
            }}
            onClick={() => openLightbox({
              src: require("../../assets/images/tech/phase8_langraph_flow.svg").default,
              alt: "Chefbelle Architecture Diagram",
              caption: "Detailed system architecture diagram showing the components and data flow of Chefbelle"
            })}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: '10px', 
                right: '10px', 
                bg: 'rgba(255,255,255,0.7)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'small',
                zIndex: 10,
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  bg: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#333333"/>
              </svg>
            </Box>
            <img
              src={require("../../assets/images/tech/phase8_langraph_flow.svg").default}
              alt="Chefbelle Architecture Diagram"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Card>
        </Box>

        {/* Technical Phases */}
        <Box sx={{ my: 5 }}>
          <Heading as="h2" sx={{ textAlign: 'center', mb: 4, pt: 4, color: '#FFF' }}>
            Technical Implementation Phases
          </Heading>

          {technicalBlogSections.map((section) => {
            // Different light background colors for each phase
            const phaseColors = [
              '#F0F8FF', // Alice Blue
              '#F0FFFF', // Azure
              '#F5F5DC', // Beige
              '#E6E6FA', // Lavender
              '#FFF0F5', // Lavender Blush
              '#F0FFF0', // Honeydew
              '#F5FFFA', // Mint Cream
              '#FFF5EE', // Seashell
              '#F5F5F5', // White Smoke
              '#FFFAF0'  // Floral White
            ];
            const phaseBgColor = phaseColors[(section.phase - 1) % phaseColors.length];

            return (
              <Card
                key={section.phase}
                variant="card"
                sx={{
                  mb: 4,
                  border: expandedPhase === section.phase ? '2px solid' : '1px solid',
                  borderColor: expandedPhase === section.phase ? 'primary' : 'gray',
                  transition: 'all 0.3s ease',
                  bg: expandedPhase === section.phase ? 'highlight' : phaseBgColor,
                  color: '#111', // Force dark font color for all text in the card
                }}
              >
              {/* Phase Header */}
              <Flex
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 3,
                  cursor: 'pointer',
                  bg: expandedPhase === section.phase ? 'highlight' : 'white',
                  color: '#111', // Dark font for header
                }}
                onClick={() => togglePhase(section.phase)}
              >
                <Flex sx={{ alignItems: 'center' }}>
                  <Badge
                    variant="phaseBadge"
                    sx={{
                      mr: 3,
                      bg: expandedPhase === section.phase ? 'primary' : 'gray',
                      color: '#fff',
                    }}
                  >
                    Phase {section.phase}
                  </Badge>
                  <Heading as="h3" sx={{ m: 0, fontSize: [2, 3], color: '#111' }}>
                    {section.title} {section.emoji}
                  </Heading>
                </Flex>
                <Box sx={{ fontSize: 3, color: '#111' }}>
                  {expandedPhase === section.phase ? '▲' : '▼'}
                </Box>
              </Flex>

              {/* Expanded Content */}
              {expandedPhase === section.phase && (
                <Box sx={{ p: 4, pt: 2, borderTop: '1px solid', borderColor: 'gray', color: '#222' }}>
                  <Text sx={{ fontSize: [1, 2], mb: 4, color: '#222' }}>
                    {section.content}
                  </Text>

                  {/* Code Snippet */}
                  {section.code && (
                    <Box sx={{ my: 4 }}>
                      <Heading as="h4" sx={{ fontSize: 2, mb: 2, color: '#111' }}>
                        Code Implementation:
                      </Heading>
                      <Box sx={{ color: '#222' }}>
                        <CodeBlock
                          language={section.code.language}
                          code={section.code.snippet}
                          fileName={section.code.fileName}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Images */}
                  {section.images && section.images.length > 0 && (
                    <Box sx={{ my: 4 }}>
                      <Heading as="h4" sx={{ fontSize: 2, mb: 3, color: '#111' }}>
                        Visual Insights:
                      </Heading>
                      <Flex
                        sx={{
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          gap: 3,
                          color: '#222',
                        }}
                      >
                        {section.images.map((image, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: ['100%', 'calc(50% - 12px)', image.wide ? '100%' : 'calc(50% - 12px)'],
                              textAlign: 'center',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                cursor: 'pointer'
                              },
                              color: '#222',
                            }}
                            onClick={() => openLightbox(image)}
                          >
                            <Box sx={{ overflow: 'hidden', borderRadius: 'default', boxShadow: 'small' }}>
                              <img
                                src={image.src}
                                alt={image.alt}
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                              />
                            </Box>
                            {image.caption && (
                              <Text sx={{ fontSize: 1, color: '#222', mt: 2, fontStyle: 'italic' }}>
                                {image.caption}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  )}
                </Box>
              )}
            </Card>
          )})}
        </Box>

        {/* Conclusion */}
        <Card variant="card" sx={{ p: 4, bg: 'highlight', mt: 5 }}>
          <Heading as="h2" sx={{ textAlign: 'center', mb: 3, color: '#333' }}>
            Conclusion: Cooking Smarter with GenAI 🚀
          </Heading>
          <Text sx={{ fontSize: [1, 2], color: '#333' }}>
            Chefbelle successfully demonstrates how combining Large Language Models (Gemini), Retrieval Augmented Generation (via database/API tools),
            semantic search (ChromaDB), and agentic orchestration (LangGraph) can create a genuinely useful solution to the common
            "what's for dinner?" problem. By grounding responses in fetched data, understanding user context, and leveraging specialized tools,
            Chefbelle moves beyond simple chatbots towards intelligent, interactive assistance. This technical journey showcases the power of
            modern AI techniques to build applications that are not only innovative but also address tangible, everyday challenges.
          </Text>
        </Card>

        {/* Image Lightbox */}
        {lightboxOpen && lightboxImage && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bg: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              p: 4
            }}
            onClick={closeLightbox}
          >
            <Box 
              sx={{ 
                position: 'relative', 
                width: '90%',
                height: '90%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box sx={{ 
                overflowY: 'auto', 
                overflowX: 'auto',
                width: '100%',
                height: lightboxImage.caption ? 'calc(100% - 80px)' : '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: '4px'
              }}>
                <img
                  src={lightboxImage.src}
                  alt={lightboxImage.alt}
                  style={{ 
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '1000px', 
                    maxHeight: '1000px',
                    objectFit: 'contain',
                    margin: '0 auto', 
                    display: 'block',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                />
              </Box>
              {lightboxImage.caption && (
                <Box sx={{
                  bg: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  p: 3,
                  mt: 2,
                  borderRadius: 'default',
                  fontStyle: 'italic',
                  maxWidth: '1000px',
                  width: '100%'
                }}>
                  {lightboxImage.caption}
                </Box>
              )}
              <Button
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  bg: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  '&:hover': { 
                    bg: 'rgba(0,0,0,0.9)',
                    transform: 'scale(1.1)',
                    border: '2px solid rgba(255,255,255,0.6)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={closeLightbox}
              >
                ✕
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

// --- Main TechDeepDive component (Updated with multi-carousel logic) ---
const TechDeepDive = () => {
  // State for controlling the modal visibility and content (FROM TechDeepDive_multi_crousel.js)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Open modal with the selected section (FROM TechDeepDive_multi_crousel.js)
  const openModal = (section) => {
    setActiveSection(section);
    setCurrentSlide(0); // Reset slide index when opening
    setIsModalOpen(true);
  };

  // Close the modal (FROM TechDeepDive_multi_crousel.js)
  const closeModal = () => {
    setIsModalOpen(false);
    setActiveSection(null); // Clear active section on close
  };

  // Slide navigation within the modal (FROM TechDeepDive_multi_crousel.js)
  const nextSlide = () => {
    if (activeSection && activeSection.slides.length > 0) {
      setCurrentSlide((prev) =>
        prev === activeSection.slides.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevSlide = () => {
    if (activeSection && activeSection.slides.length > 0) {
      setCurrentSlide((prev) =>
        prev === 0 ? activeSection.slides.length - 1 : prev - 1
      );
    }
  };

  return (
    // Using a generic class name, adjust if specific styling is needed
    <div className="tech-deep-dive-container page-section">
      {/* Main Page Heading */}
      <h1 className="tech-page-heading" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        Technology Deep Dive 🚀✨
      </h1>

      {/* Introduction text (FROM TechDeepDive_multi_crousel.js) */}
      <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 2rem' }}>
        <p style={{
          fontSize: '1.2rem',
          color: '#FFFFFF', // White text for contrast
          textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
          backgroundColor: 'rgba(100, 89, 207, 0.8)', // Using primary color with alpha
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          fontWeight: '500'
        }}>
          Curious about how our Kitchen Assistant works behind the scenes?
          Explore these key technologies that power our intelligent recipe recommendations!
        </p>
      </div>

      {/* Tech Section Carousel (FROM TechDeepDive_multi_crousel.js) */}
      <div style={{ margin: '3rem 0' }}>
        <Carousel
          responsive={responsive}
          infinite={true}
          autoPlay={true}
          autoPlaySpeed={5000}
          keyBoardControl={true}
          customTransition="transform 500ms ease-in-out"
          transitionDuration={500}
          containerClass="carousel-container"
          removeArrowOnDeviceType={["tablet", "mobile"]}
          dotListClass="custom-dot-list-style"
          itemClass="carousel-item-padding-40-px" // Default padding class
        >
          {techSections.map((section) => (
            <div
              key={section.id}
              style={{
                backgroundColor: section.color,
                borderRadius: '12px',
                padding: '2rem',
                margin: '1rem', // Added margin between cards
                height: '420px', // Fixed height for all cards
                minHeight: '420px',
                maxHeight: '420px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between', // Distribute content vertically
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'pointer',
                textAlign: 'center', // Center text content
                overflow: 'hidden', // Prevent overflow
              }}
              onClick={() => openModal(section)}
              onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
              }}
            >
              {/* Icon */}
              <div className="tech-section-icon" style={{ marginBottom: '1rem', transform: 'scale(1.2)' }}> {/* Slightly larger icon */}
                {section.icon}
              </div>
              {/* Title */}
              <h3 style={{ color: '#333', fontSize: '1.4rem', marginBottom: '0.8rem' }}> {/* Darker text */}
                {section.title}
              </h3>
              {/* Description */}
              <p style={{ flexGrow: 1, marginBottom: '1rem', color: '#555', fontSize: '1rem', overflow: 'auto' }}> {/* Darker text */}
                {section.description}
              </p>
              {/* Button */}
              <div style={{ marginTop: 'auto' }}> {/* Pushes button down */}
                 <button
                  style={{
                    backgroundColor: '#6459CF', // Using theme primary color
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '0.6rem 1.2rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 8px rgba(100, 89, 207, 0.3)', // Shadow using primary color
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4B0082'} // Darker shade on hover
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6459CF'}
                >
                  Learn More
                </button>
              </div>
            </div>
          ))}
        </Carousel>
      </div>

      {/* Modal for detailed slides (FROM TechDeepDive_multi_crousel.js - adapted) */}
      <SlideModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={activeSection ? `${activeSection.title} - Deep Dive` : ''}
      >
        {activeSection && activeSection.slides.length > 0 && (
          <div>
            {/* Slide content */}
            <div
              className="slide-detail"
              style={{
                backgroundColor: activeSection.color || '#f8f8f8',
                padding: '2rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                minHeight: '400px',
                height: '400px',
                maxHeight: '50vh',
                overflowY: 'auto',
                border: '1px solid #eee',
                boxSizing: 'border-box'
              }}
            >
              <h3 style={{ color: '#4B0082', marginBottom: '1rem', textAlign: 'center' }}>
                {activeSection.slides[currentSlide].title}
              </h3>
              {activeSection.slides[currentSlide].content.map((text, index) => (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: text }}
                  style={{
                    margin: '0.8rem 0',
                    fontSize: '1.1rem',
                    color: '#333',
                    lineHeight: '1.6',
                  }}
                />
              ))}
            </div>
            {/* Navigation controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', padding: '0 1rem' }}>
              <button
                onClick={prevSlide}
                style={{
                  background: '#6459CF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4B0082'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6459CF'}
              >
                ⬅️ Previous
              </button>
              <span style={{ color: '#4B0082', fontWeight: 'bold' }}>
                {currentSlide + 1} / {activeSection.slides.length}
              </span>
              <button
                onClick={nextSlide}
                style={{
                  background: '#6459CF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4B0082'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6459CF'}
              >
                Next ➡️
              </button>
            </div>
          </div>
        )}
      </SlideModal>

      {/* Add new Technical Blog Section below carousel (Keep this unchanged) */}
      <div className="technical-blog-section">
        <TechnicalBlog />
      </div>

      {/* Floating Menu (      {/* Floating Menu (Keep this) */}
      <FloatingMenu />
    </div>
  );
};

export default TechDeepDive;